<template>
  <div 
    @click="navigateToProject"
    class="p-4 rounded-lg border border-solid border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200 h-full group cursor-pointer"
    role="article"
  >
      <div class="flex flex-col gap-3 w-full h-full">
        <!-- Header: Icon, Title, Status, Chevron -->
        <div class="flex items-start gap-3 w-full">
          <!-- Project Icon -->
          <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
            <Icon name="project" class="w-4 h-4 text-white" />
          </div>
          
          <!-- Title and Status -->
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <!-- Title with Status Badge -->
            <div class="flex items-center gap-2">
              <h5 class="text-base font-semibold text-gray-900 dark:text-white leading-tight m-0 truncate">
                {{ project.title }}
              </h5>
              <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', getStatusBadgeClasses(project.status)]">
                {{ formatStatus(project.status) }} 
              </span>
            </div>
            
            <!-- Description -->
            <div class="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 mb-4">
              {{ project.description  }}
            </div>
            
            <!-- Counts as Badges -->
            <div class="flex items-center gap-2 flex-wrap">
              <template v-if="statsLoading">
                <span v-for="i in 4" :key="i" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">
                  <div class="w-12 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </span>
              </template>
              <template v-else>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                <Icon name="feature" class="w-3 h-3 mr-1 text-cyan-600 dark:text-cyan-400" />
                {{ project.features || 0 }} features
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                <Icon name="testCase" class="w-3 h-3 mr-1 text-emerald-600 dark:text-emerald-400" />
                {{ project.testCases || 0 }} test cases
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                <Icon name="testRun" class="w-3 h-3 mr-1 text-purple-600 dark:text-purple-400" />
                {{ project.testRuns || 0 }} test runs
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                <Icon name="bug" class="w-3 h-3 mr-1 text-red-600 dark:text-red-400" />
                {{ project.bugs || 0 }} bugs
              </span>
              </template>
            </div>
          </div>
          
          <!-- Chevron Icon -->
          <Icon 
            name="chevronRight" 
            class="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex-shrink-0" 
            aria-hidden="true" 
          />
        </div>
      </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { HLBadge } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import { formatStatus } from '@/utils/badgeHelpers'

const router = useRouter()

const props = defineProps({
  project: {
    type: Object,
    required: true
  },
  statsLoading: {
    type: Boolean,
    default: false
  }
})

const projectRoute = computed(() => `/projects/${props.project.id}`)

// Navigate to project
const navigateToProject = () => {
  router.push(projectRoute.value)
}

// Status badge color mapping for HLBadge
const getStatusBadgeColor = (status) => {
  const statusLower = status?.toLowerCase() || 'active'
  
  const statusColorMap = {
    'active': 'green',
    'paused': 'indigo',
    'inactive': 'gray',
    'completed': 'purple',
    'on_hold': 'yellow',
    'archived': 'gray',
    'draft': 'blue'
  }
  
  return statusColorMap[statusLower] || 'green'
}

// Status badge classes for custom badges
const getStatusBadgeClasses = (status) => {
  const statusLower = status?.toLowerCase() || 'active'
  
  const statusClassMap = {
    'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    'paused': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200',
    'inactive': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200',
    'completed': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200',
    'on_hold': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
    'archived': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200',
    'draft': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
  }
  
  return statusClassMap[statusLower] || 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
}
</script>


